import BaseModal from 'components/Modals/BaseModal';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Nav } from 'react-bootstrap';
import styles from '../css/ServiceManager.module.css';

interface ServiceManagerHelpModalProps {
  show: boolean;
  onClose: () => void;
  /** Whether the Deployments tab is available to this user. */
  showDeployments?: boolean;
}

type HelpTabKey = 'overview' | 'services' | 'deployments' | 'expect';

/** A term and its explanation, rendered as a labelled row. */
function Term({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-2">
      <span className="text-theme fw-semibold">{label}</span>
      <span className="text-theme-muted"> &mdash; {children}</span>
    </div>
  );
}

/** Lead paragraph for a panel. */
function Lead({ children }: { children: ReactNode }) {
  return <p className="text-theme-muted mb-3">{children}</p>;
}

/** A word the UI shows verbatim, so it can be matched against the screen. */
function UiWord({ children }: { children: ReactNode }) {
  return <span className="text-theme">{children}</span>;
}

function OverviewPanel({ showDeployments }: { showDeployments: boolean }) {
  return (
    <>
      <Lead>
        The Service Manager monitors and controls the background services behind Fusion. They run
        under PM2, and a supervisor process decides which of them run where.
      </Lead>
      <Term label="Services tab">
        Day-to-day control of processes that are already set up to run: start, stop, restart, and
        the audit trail of who did what.
      </Term>
      {showDeployments && (
        <Term label="Deployments tab">
          Which services each deployment is set up to run <em>at all</em>. This is where you turn a
          service on for the first time, and the only place a service that is switched off appears.
        </Term>
      )}
      <div className="text-theme fw-semibold border-bottom border-secondary pb-2 mb-2 mt-4">
        The one distinction worth learning
      </div>
      <Term label="Configured">
        What a deployment is <em>set up</em> to run. It persists across restarts.
      </Term>
      <Term label="Live (PM2)">Whether a process is actually up right now.</Term>
      <p className="text-theme-muted mb-0">
        These are separate questions. Turning a service on is not the same as it successfully
        starting, which is why the two are never merged into a single indicator.
      </p>
    </>
  );
}

function ServicesPanel() {
  return (
    <>
      <Lead>
        Everything listed here is already configured to run. A service that is switched off has no
        process and does not appear on this tab.
      </Lead>
      <Term label="Status">
        The live process state, as a coloured dot and a word: <UiWord>Running</UiWord>,{' '}
        <UiWord>Stopped</UiWord>, <UiWord>Restarting</UiWord> or <UiWord>Unknown</UiWord>.
      </Term>
      <Term label="Unstable Restarts">
        Restarts PM2 triggered by itself because the process crashed or exited. A climbing number
        means an unhealthy service.
      </Term>
      <Term label="Manual Restarts">Restarts an operator triggered from this screen.</Term>
      <Term label="CPU / Memory">
        Expand a row on desktop, or open the card on mobile, to see current usage.
      </Term>
      <Term label="Start / Stop / Restart">
        Act on the running process. Each asks for a reason first, which is recorded in the command
        history.
      </Term>
      <Term label="Services you cannot act on">
        A row says which reason applies: <UiWord>Locked by the supervisor</UiWord>,{' '}
        <UiWord>Monitored only, not controlled from here</UiWord> (the supervisor reports on it but
        never starts or stops it, so only Restart is offered), or a protected system service.
      </Term>
      <Term label="Service Command History">
        Every start, stop and restart, with who ran it and the reason they gave.
      </Term>
    </>
  );
}

function DeploymentsPanel() {
  return (
    <>
      <Lead>
        Each deployment is listed with the services it is set up to run. Expand one to see them.
      </Lead>
      <Term label="Turning a service on or off">
        The switch in the <UiWord>Configured</UiWord> column. This is what decides whether a
        deployment runs a service at all, and it is the only way to run one that has never run.
      </Term>
      <Term label="Live (PM2)">
        What PM2 actually has. <UiWord>Not in PM2</UiWord> means PM2 was asked and holds no such
        process. <UiWord>PM2 unreachable</UiWord> means PM2 could not be asked, so nothing in that
        column can be trusted.
      </Term>
      <Term label="Enabled, but not running">
        The flag is on and the service failed to start, usually because it is missing configuration
        it needs. Check the process logs rather than toggling again.
      </Term>
      <Term label="Source">
        <UiWord>inherits global</UiWord> means this deployment follows the global default.{' '}
        <UiWord>overridden here</UiWord> means someone set it explicitly for this deployment, which
        wins over the default.
      </Term>
      <Term label="Deployment mode">
        Only a <UiWord>managed</UiWord> deployment acts on these switches. In observe, standalone or
        disabled mode the setting is still saved, but the supervisor will not start the process. A
        banner on the deployment says so.
      </Term>
      <Term label="Add / Edit / Remove deployment">
        Creates, changes or retires a deployment. These need a supervisor restart, and a banner
        appears with a button to do it.
      </Term>
      <Term label="Global services">
        One shared instance per supervisor rather than one per deployment. Switching these is not
        available from Fusion yet; the row says so if you try.
      </Term>
    </>
  );
}

function ExpectPanel({ showDeployments }: { showDeployments: boolean }) {
  return (
    <>
      <Lead>What happens after you act, so you know whether to wait or to investigate.</Lead>
      {showDeployments && (
        <>
          <Term label="Turning a service on">
            Takes effect within about ten seconds. No supervisor restart is needed. The row then
            reports whether the process actually started.
          </Term>
          <Term label="Turning a service off">
            The process stops immediately, as part of the request. The supervisor will not start it
            again, and its next restart removes any copy that is still running.
          </Term>
          <Term label="When a restart really is needed">
            Only for adding, editing or removing a deployment. Individual services never need one.
          </Term>
        </>
      )}
      <Term label="Start / Stop / Restart on the Services tab">
        These act on the process now. They do not change what the deployment is set up to run, so a
        service that is switched off cannot be started this way.
      </Term>
      <Term label="If a change does not appear">
        Use <UiWord>Refresh</UiWord>. The list is a snapshot from when it was last requested, and
        the timestamp beside the title says when that was.
      </Term>
      <Term label="Everything is recorded">
        Each action is written to the command history with your account and the reason you gave.
      </Term>
    </>
  );
}

/**
 * Help reference for the Service Manager.
 *
 * Split into tabs rather than one long page: the previous version was a single
 * scrolling column covering both tabs at once, and an operator looking for one
 * answer had to read past everything else to find it. Each panel here is short
 * enough to read without scrolling.
 */
export default function ServiceManagerHelpModal({
  show,
  onClose,
  showDeployments = false,
}: ServiceManagerHelpModalProps) {
  const [activeTab, setActiveTab] = useState<HelpTabKey>('overview');

  // Always open on the overview rather than wherever the last visit ended.
  useEffect(() => {
    if (show) setActiveTab('overview');
  }, [show]);

  const tabs = useMemo(
    () =>
      [
        { key: 'overview' as const, label: 'Overview' },
        { key: 'services' as const, label: 'Services tab' },
        ...(showDeployments ? [{ key: 'deployments' as const, label: 'Deployments tab' }] : []),
        { key: 'expect' as const, label: 'What to expect' },
      ] satisfies { key: HelpTabKey; label: string }[],
    [showDeployments],
  );

  return (
    <BaseModal
      show={show}
      handleShow={onClose}
      size="lg"
      title="Service Manager help"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={(key) => setActiveTab((key as HelpTabKey) ?? 'overview')}
        className={`mb-3 ${styles.tabs}`}
      >
        {tabs.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {/* A floor on the height so switching tabs does not resize the dialog
          under the pointer. */}
      <div className={styles.helpPanel}>
        {activeTab === 'overview' && <OverviewPanel showDeployments={showDeployments} />}
        {activeTab === 'services' && <ServicesPanel />}
        {activeTab === 'deployments' && showDeployments && <DeploymentsPanel />}
        {activeTab === 'expect' && <ExpectPanel showDeployments={showDeployments} />}
      </div>
    </BaseModal>
  );
}
